import React, { useState,useEffect,useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from '../../plugins/axios'
import { useTranslation } from 'react-i18next'

import { checkEmptyValue } from '../../plugins/common'
import WaveCanvas from "./leadform/waveCanvas";
import MetricTable from "./leadform/metricTable";

import "./A4.scss";

const LeadForm = () => {
    const { t, i18n } = useTranslation()
    const [searchParams] = useSearchParams();
    const isFirstRender = useRef(true);
    const [queryParams, setQueryParams] = useState({})
    const [patientInfo, setPatientInfo] = useState(null);
    const [waveData, setWaveData] = useState({})
    const [metricData, setMetricData] = useState({})
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [isPageReady, setIsPageReady] = useState(false);

    const typeByLeadLabel = {
        'I': 'Ⅰ',
        'II': 'Ⅱ',
        'III': 'Ⅲ',
        'LEADⅠ': 'Ⅰ',
        'LEADⅡ': 'Ⅱ',
        'LEADⅢ': 'Ⅲ'
    }

    //get Patient Info & 다국어를 위한 user 정보
    const getPatientInfo = async () => {
        try{
            // 유저 정보 가져오기
            const resUser = await axios.get('/user/me')
            if (resUser.code != '200') throw resUser

            //언어 세팅
            i18n.changeLanguage(resUser.data.language);
            const res = await axios.get(`/trend/patient/info/file?fileId=${queryParams.fileId}`)
            if(res.code != '200') throw res;
            
            setPatientInfo(res.data);
        }
        catch(err){
            console.warn('getPatientInfo err : ',err);
        }
    }

    // get Wave & Metric Data
    const getData = async () => {
        try{
            if(queryParams.type == 'wave'){
                const waveRes = await axios.get(`/trend/waveforms/file?fileId=${queryParams.fileId}`)
                if (waveRes.code != '200') throw waveRes
                const newWaveData = {}
                waveRes.data.waveformData.leads.forEach(item => {
                    newWaveData[typeByLeadLabel[item.leadLabel] || item.leadLabel] = {
                        ...item,
                        wave: item.vals.slice(0, 5000)
                    }
                })
                setWaveData(newWaveData)
            }else if(queryParams.type == 'value'){
                const MetricRes = await axios.get(`/trend/metric/file?fileId=${queryParams.fileId}`)
                if (MetricRes.code != '200') throw MetricRes
                const newMetricData = {}
                MetricRes.data.leadMetric.forEach(item => {
                    newMetricData[typeByLeadLabel[item.lead] || item.lead] = item
                })
                setMetricData(newMetricData)
            }
            return true;
        }
        catch(err){
            return false;            
        }
    }

    // 프린터 함수
    const handlePrint = () => {
        console.log('프린트 시작');
        
        // 클래스 제거 후 다시 추가 (리프레시 효과)
        document.documentElement.classList.remove("waveform-active");
        document.body.classList.remove("waveform-active");
        
        setTimeout(() => {
            document.documentElement.classList.add("waveform-active");
            document.body.classList.add("waveform-active");
            
            setTimeout(() => {
                console.log('페이지 수:', document.querySelectorAll('.a4-page').length);
                window.print();
            }, 500);
        }, 100);
    };

    useEffect(() => {    

        const params = JSON.parse(searchParams.get('params'))

        setQueryParams({
            createdAt : params.createdAt || '',
            fileId : params.fileId || '',
            type : params.type || '',
            format : params.format || '',
            lead : params.lead || '',
        });        

        //Page 스타일링
        const style = document.createElement("style");
            style.innerHTML = `
            @page {
                size: 3508px 2480px;
                margin: 0;
            }
            `;
        document.head.appendChild(style);

        // 스타일 클래스 추가
        document.documentElement.classList.add("waveform-active");
        document.body.classList.add("waveform-active");        

        return () => {
            document.documentElement.classList.remove("waveform-active");
            document.body.classList.remove("waveform-active");
            document.head.removeChild(style); // 라우트 빠져나갈 때 제거
        };
    }, []);

    useEffect(() => {
        if(queryParams.type){
            const loadAllData = async () => {
                try {
                    const [data] = await Promise.all([
                        getData() //환자 history 가져오기
                    ]);

                    if (data) {
                        console.log('모든 데이터 로딩 완료');
                        setIsDataLoaded(true);
                    } else {
                        console.error('데이터 로딩 실패');
                    }
                } catch (error) {
                    console.error('데이터 로드 중 에러:', error);
                }
            };
            loadAllData();
        }
    }, [waveData, metricData]);

    useEffect(() => {
        if(isFirstRender.current){
            isFirstRender.current = false;
            return;
        }
        
        getPatientInfo();
        getData();
    }, [queryParams])

    useEffect(() => {
        if (isDataLoaded) {
            // DOM 렌더링 및 Canvas 그리기 완료를 위한 충분한 대기 시간
            const renderTimer = setTimeout(() => {
                //console.log('페이지 렌더링 완료');
                setIsPageReady(true);
            }, 1500); // Canvas 렌더링을 고려한 여유 시간

            return () => clearTimeout(renderTimer);
        }
    }, [isDataLoaded]);

    //페이지 준비 완료 시 자동 프린트
    useEffect(() => {
        if (isPageReady) {
            // URL 파라미터로 자동 프린트 제어
            const urlParams = new URLSearchParams(window.location.search);
            const autoPrint = urlParams.get('autoPrint');
            const delay = parseInt(urlParams.get('delay')) || 3000;
            
            if (autoPrint === 'true' || autoPrint === null) { // 기본값은 자동 프린트
                setTimeout(() => {
                    handlePrint(); // 자동 프린트 잠시 스탑
                }, delay);
            }
        }
    }, [isPageReady]);

    //인쇄 하거나 취소하고 창 꺼지기
    window.onafterprint = () => {
        setTimeout( () => {
            window.close();
        },1000)
    };
    
    // 스타일 정의
    const patientInfoFontBold = 'text-black font-medium text-[48px] leading-[110%] font-pretendard';
    const patientInfoFont = 'text-black font-light text-[36px] leading-[40px] font-pretendard';    

    return (
        <div className="document-container bg-gray-100 p-20 font-sans min-h-screen">
            <div className="a4-page-landscape mx-auto bg-white shadow-lg relative box-border">
                <div className="w-full h-full flex flex-col px-[146px] pt-[140px] pb-[66px]">                    
                    <div>
                        <div className="w-full flex flex-col pb-[24px] border-b-[4px] border-[#5E5E5E] ">
                            <div className='w-full flex items-center justify-start gap-[122px]'>
                                <div className="flex gap-[12px]">
                                    <strong className={patientInfoFontBold}>{queryParams.createdAt}</strong>                                    
                                </div>                                
                            </div>
                        </div>                                               
                    </div>
                    <div className={`flex justify-start mt-[23px] ${queryParams.type == 'wave' ? 'mb-[80px]' : 'mb-[40px]'}`}>
                        <div className="w-[775px] flex flex-col justify-start items-start">
                            <div className="w-full flex justify-evenly items-center gap-[20px] mb-[24px]">
                                <div className="flex-1 flex justify-between items-center">
                                    <span className={patientInfoFont}>{t('print.common.patientId')}</span>
                                    <span className={patientInfoFont}>:</span>
                                </div>
                                <div className="flex-1 flex justify-start items-center">
                                    <span className={patientInfoFont}>{checkEmptyValue(patientInfo?.devicePatientId)}</span>                                
                                </div>
                            </div>
                            <div className="w-full flex justify-evenly items-center gap-[20px] mb-[24px]">
                                <div className="flex-1 flex justify-between items-center">
                                    <span className={patientInfoFont}>{t('print.common.name')}</span>
                                    <span className={patientInfoFont}>:</span>
                                </div>
                                <div className="flex-1 flex justify-start items-center">
                                    <span className={patientInfoFont}>{checkEmptyValue(patientInfo?.patientName)}</span>                                
                                </div>
                            </div>
                            <div className="w-full flex justify-evenly items-center gap-[20px] mb-[24px]">
                                <div className="flex-1 flex justify-between items-center">
                                    <span className={patientInfoFont}>{t('print.common.dateOfBirth')}</span>
                                    <span className={patientInfoFont}>:</span>
                                </div>
                                <div className="flex-1 flex justify-start items-center">
                                    <span className={patientInfoFont}>{patientInfo?.birthDay === 'none' ? '-' : checkEmptyValue(patientInfo?.birthDay)}</span>                                
                                </div>
                            </div>
                            <div className="w-full flex justify-evenly items-center gap-[20px] mb-[24px]">
                                <div className="flex-1 flex justify-between items-center">
                                    <span className={patientInfoFont}>{t('print.common.gender')}</span>
                                    <span className={patientInfoFont}>:</span>
                                </div>
                                <div className="flex-1 flex justify-start items-center">
                                    <span className={patientInfoFont}>{patientInfo?.gender === 'M' ? t('print.common.male') : patientInfo?.gender === 'F' ? t('print.common.female') : t('print.common.notSelected')}</span>                                
                                </div>
                            </div>
                        </div>
                        <div className="w-[775px] flex flex-col justify-center items-start">
                            <div className="w-full flex justify-evenly items-center gap-[20px] mb-[24px]">
                                <div className="flex-1 flex justify-between items-center">
                                    <span className={patientInfoFont}>{t('print.leadForm.ventrate')}</span>
                                    <span className={patientInfoFont}>:</span>
                                </div>
                                <div className="flex-1 flex justify-start items-center">
                                    <span className={patientInfoFont}>{`${checkEmptyValue(patientInfo?.ventrate)} bpm`}</span>                                
                                </div>
                            </div>
                            <div className="w-full flex justify-evenly items-center gap-[20px] mb-[24px]">
                                <div className="flex-1 flex justify-between items-center">
                                    <span className={patientInfoFont}>{t('print.leadForm.ptDuration')}</span>
                                    <span className={patientInfoFont}>:</span>
                                </div>
                                <div className="flex-1 flex justify-start items-center">
                                    <span className={patientInfoFont}>{`${checkEmptyValue(patientInfo?.pdurationms)}/${checkEmptyValue(patientInfo?.tdurationms)} ms`}</span>                                
                                </div>
                            </div>
                            <div className="w-full flex justify-evenly items-center gap-[20px] mb-[24px]">
                                <div className="flex-1 flex justify-between items-center">
                                    <span className={patientInfoFont}>{t('print.leadForm.qrsDuration')}</span>
                                    <span className={patientInfoFont}>:</span>
                                </div>
                                <div className="flex-1 flex justify-start items-center">
                                    <span className={patientInfoFont}>{`${checkEmptyValue(patientInfo?.qrsms)} ms`}</span>                                
                                </div>
                            </div>
                            <div className="w-full flex justify-evenly items-center gap-[20px] mb-[24px]">
                                <div className="flex-1 flex justify-between items-center">
                                    <span className={patientInfoFont}>{t('print.leadForm.qtDuration')}</span>
                                    <span className={patientInfoFont}>:</span>
                                </div>
                                <div className="flex-1 flex justify-start items-center">
                                    <span className={patientInfoFont}>{`${checkEmptyValue(patientInfo?.qtms)}/${checkEmptyValue(patientInfo?.qtcms)} ms`}</span>                                
                                </div>
                            </div>
                            <div className="w-full flex justify-evenly items-center gap-[20px] mb-[24px]">
                                <div className="flex-1 flex justify-between items-center">
                                    <span className={patientInfoFont}>{t('print.leadForm.ppprInterval')}</span>
                                    <span className={patientInfoFont}>:</span>
                                </div>
                                <div className="flex-1 flex justify-start items-center">
                                    <span className={patientInfoFont}>{`${checkEmptyValue(patientInfo?.prms)}/${checkEmptyValue(patientInfo?.qtms)} ms`}</span>                                
                                </div>
                            </div>
                            <div className="w-full flex justify-evenly items-center gap-[20px] mb-[24px]">
                                <div className="flex-1 flex justify-between items-center">
                                    <span className={patientInfoFont}>{t('print.leadForm.pqrsTaxis')}</span>
                                    <span className={patientInfoFont}>:</span>
                                </div>
                                <div className="flex-1 flex justify-start items-center">
                                    <span className={patientInfoFont}>{`${checkEmptyValue(patientInfo?.paxis)} / ${checkEmptyValue(patientInfo?.qrsaxis)} / ${checkEmptyValue(patientInfo?.taxis)} deg`}</span>                                
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Content */}
                    <div> 
                        {
                            queryParams && queryParams.type == 'wave' && waveData && (
                                <WaveCanvas waveData={waveData} format={queryParams.format} lead={queryParams.lead} />
                            )
                        }
                        {
                            queryParams && queryParams.type == 'value' && metricData && (
                                <MetricTable metricData={metricData} typeByLeadLabel={typeByLeadLabel}/>
                            )
                        }

                    </div>
                </div>
            </div>        
        </div>
    )
}

export default LeadForm